import type { Stage } from '../../types';

export const pregnancy1En: Stage = {
  id: 'pregnancy-1',
  key: '1',
  title: 'Early Pregnancy',
  subtitle: 'Embracing a tiny new life',
  weeks: 'Up to 13 weeks',
  description:
    'Morning sickness and physical changes can be intense. Take it easy and prepare the necessary procedures and information one step at a time.',
  mindCards: [
    {
      id: 'm1',
      title: 'Make your condition your ally',
      body: 'Sleep when sleepy, eat what you want. It is okay if every day is not the same.',
      type: 'body',
    },
    {
      id: 'm2',
      title: 'Move forward one small step at a time',
      body: 'The maternity handbook and hospital procedures are enough if you do one thing at a time from today.',
      type: 'mind',
    },
    {
      id: 'm3',
      title: 'Together with your partner',
      body: 'Share the news and talk about your future roles as a couple.',
      type: 'mind',
    },
    {
      id: 'm4',
      title: 'Do not hide your worries',
      body: 'It is natural to feel confused by physical and emotional changes. Decide who you can consult.',
      type: 'mind',
    },
    {
      id: 'm5',
      title: 'Start recording medical expenses',
      body: 'Keep receipts and records together in one place. Later procedures will be smoother.',
      type: 'life',
    },
    {
      id: 'm6',
      title: 'Continue folic acid',
      body: 'Supplement with food and supplements, and continue while consulting your doctor or pharmacist.',
      type: 'body',
    },
    {
      id: 'm7',
      title: 'Receive information through Boshi-Mo',
      body: 'Register for the Niigata child-rearing support app "Boshi-Mo" and check notifications and consultation windows. (Example: Niigata City)',
      type: 'life',
    },
    {
      id: 'm8',
      title: 'Avoid secondhand smoke',
      body: 'Including heated tobacco, ask people not to smoke near pregnant women or infants.',
      type: 'partner',
    },
  ],
  checklist: [
    { id: 'c1', text: 'Receive the maternity handbook and fill in necessary items', category: 'admin', note: 'You can apply at your local city/ward office. In Niigata City, registration for the Boshi-Mo app is also guided.' },
    { id: 'c2', text: 'Choose an obstetrics/gynecology clinic and schedule the first checkup', category: 'hospital', note: 'Confirm accessibility and night-time response.' },
    { id: 'c3', text: 'Check folic acid supplements and dietary balance', category: 'body', note: 'From before pregnancy through early pregnancy, aim for 400 micrograms per day (varies by individual).' },
    { id: 'c4', text: 'Check guidelines for no smoking, no alcohol, and caffeine intake', category: 'body', note: 'Aim for caffeine under 200 mg per day.' },
    { id: 'c5', text: 'Consider when to report your pregnancy to your workplace', category: 'admin', note: 'Many people report after entering the stable period.' },
    { id: 'c6', text: 'Research maternity and childcare leave procedures', category: 'admin', note: 'Check company rules and employment insurance requirements.' },
    { id: 'c7', text: 'Review the outline of childbirth costs and the childbirth lump-sum allowance', category: 'admin', note: 'Confirm with your health insurance union or local government.' },
    { id: 'c8', text: 'Start keeping a pregnancy record', category: 'life', note: 'Weight, blood pressure, morning sickness condition, etc.' },
    { id: 'c9', text: 'Talk with your partner about sharing household chores', category: 'partner', note: 'Assign concrete tasks.' },
    { id: 'c10', text: 'Write down questions to ask at your first checkup', category: 'hospital', note: 'Prepare a list of questions.' },
    { id: 'c11', text: 'Incorporate morning sickness countermeasures into daily life', category: 'body', note: 'Cold foods, small frequent meals, rest, etc.' },
    { id: 'c12', text: 'Do not be surprised by emotional changes; be kind to yourself', category: 'mind', note: 'This is a period when emotional instability is common.' },
    { id: 'c13', text: 'Store medical receipts', category: 'admin', note: 'May be needed later for high-cost medical care or deductions.' },
    { id: 'c14', text: 'Prepare a maternity mark', category: 'shopping', note: 'Useful on trains and at work.' },
    { id: 'c15', text: 'Look up nearby midwifery clinics and consultation windows', category: 'hospital', note: 'Keep them in mind as emergency contacts.' },
    { id: 'c16', text: 'Share your image of childbirth with your partner', category: 'partner', note: 'Presence at birth, hometown delivery, etc.' },
    { id: 'c17', text: 'Regulate your sleep rhythm', category: 'life', note: 'Sleep without overdoing it during this tiring period.' },
    { id: 'c18', text: 'Review the burden of your commuting route', category: 'life', note: 'Adjust crowded times and times when seats are available.' },
    { id: 'c19', text: 'Aim for nutritionally balanced meals', category: 'body', note: 'Base on one soup and three dishes, with vegetables, protein, and iron in mind.' },
    { id: 'c20', text: 'Confirm the documents needed for maternity leave at your workplace', category: 'admin', note: 'A medical certificate may be required.' },
    { id: 'c21', text: 'Register for Boshi-Mo (Niigata child-rearing support app)', category: 'admin', note: 'Example for Niigata City. Other municipalities may have different app names or notification services.' },
    { id: 'c22', text: 'Apply for the medical expense subsidy for pregnant and postpartum women', category: 'admin', note: 'As soon as possible after receiving the maternity handbook. The subsidy period begins from the eligibility determination date.' },
    { id: 'c23', text: 'Check application for the first pregnancy support benefit', category: 'admin', note: 'Within two years from the date fetal heartbeat is confirmed. Example: Niigata City.' },
    { id: 'c24', text: 'Confirm National Health Insurance and National Pension pre/postnatal exemption', category: 'admin', note: 'Applications can be submitted from six months before the due date. Pension benefits will not be reduced.' },
    { id: 'c25', text: 'Aim for one soup and three dishes, and salt under 6.5 g per day', category: 'body', note: 'From mid-pregnancy, +250 kcal; late pregnancy and breastfeeding, +450 kcal as a general guideline.' },
    { id: 'c26', text: 'Consult your doctor rather than self-medicating with over-the-counter drugs or supplements', category: 'body', note: 'Medicine ingredients may affect the fetus.' },
    { id: 'c27', text: 'Continue oral care daily', category: 'body', note: 'Periodontal disease increases preterm risk, so use pregnant-woman dental checkups too.' },
    { id: 'c28', text: 'Be careful not to eat too much fish high in mercury', category: 'body', note: 'Be mindful of quantities such as once or twice a week for bigeye tuna, swordfish, southern bluefin tuna, and alfonsino.' },
  ],
  shopping: [
    {
      id: 's1',
      name: 'Maternity mark',
      priority: 'must',
      timing: 'Anytime',
      priceYenMin: 0,
      priceYenMax: 1000,
      note: 'May be free at city offices or hospitals.',
    },
    {
      id: 's2',
      name: 'Folic acid supplement',
      priority: 'must',
      timing: 'From early pregnancy',
      priceYenMin: 1500,
      priceYenMax: 4500,
      note: 'Varies by individual; consult your doctor.',
    },
    {
      id: 's3',
      name: 'Stretch-mark oil or cream',
      priority: 'nice',
      timing: 'From mid-pregnancy',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Focus on moisturizing.',
    },
    {
      id: 's4',
      name: 'Loose cotton underwear',
      priority: 'should',
      timing: 'Anytime',
      priceYenMin: 1500,
      priceYenMax: 4000,
      note: 'Choose non-constricting materials.',
    },
    {
      id: 's5',
      name: 'Warm loungewear',
      priority: 'should',
      timing: 'Anytime',
      priceYenMin: 3000,
      priceYenMax: 8000,
      note: 'Comfortable during morning sickness.',
    },
    {
      id: 's6',
      name: 'Body weight/ composition scale',
      priority: 'nice',
      timing: 'Anytime',
      priceYenMin: 2000,
      priceYenMax: 8000,
      note: 'Weight management will be needed at checkups.',
    },
    {
      id: 's7',
      name: 'Maternity diary',
      priority: 'nice',
      timing: 'Anytime',
      priceYenMin: 1000,
      priceYenMax: 3000,
      note: 'Can also be replaced with an app.',
    },
    {
      id: 's8',
      name: 'Low-irritation skincare set',
      priority: 'should',
      timing: 'Anytime',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Skin tends to become sensitive during this period.',
    },
    {
      id: 's9',
      name: 'Maternity pillow',
      priority: 'nice',
      timing: 'From mid-pregnancy',
      priceYenMin: 3000,
      priceYenMax: 10000,
      note: 'Becomes useful as your belly grows.',
    },
    {
      id: 's10',
      name: 'Non-caffeine drinks',
      priority: 'should',
      timing: 'Anytime',
      priceYenMin: 1000,
      priceYenMax: 3000,
      note: 'Barley tea, rooibos tea, etc.',
    },
    {
      id: 's11',
      name: 'Cooler eco bag',
      priority: 'nice',
      timing: 'Anytime',
      priceYenMin: 1000,
      priceYenMax: 3000,
      note: 'Convenient for hydration during visits.',
    },
    {
      id: 's12',
      name: 'Maternity or nursing bra',
      priority: 'should',
      timing: 'Mid to late pregnancy',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Prioritize comfort. Choose a design usable after birth.',
    },
    {
      id: 's13',
      name: 'Gentle oral care set (small toothbrush, mouthwash)',
      priority: 'should',
      timing: 'Anytime',
      priceYenMin: 500,
      priceYenMax: 2000,
      note: 'Use a small head or mouthwash when brushing is hard during morning sickness.',
    },
  ],
  warnings: [
    { id: 'w1', label: 'Severe abdominal pain or bleeding', level: 'urgent' },
    { id: 'w2', label: 'Feeling drowsy or losing consciousness', level: 'urgent' },
    { id: 'w3', label: 'Severe pain on one side', level: 'urgent' },
    { id: 'w4', label: 'Unable to eat anything due to morning sickness', level: 'watch' },
    { id: 'w5', label: 'Fever of 38°C or higher', level: 'watch' },
    { id: 'w6', label: 'Difficulty or pain urinating', level: 'watch' },
    { id: 'w7', label: 'Strong headache or nausea', level: 'watch' },
    { id: 'w8', label: 'Lower abdominal tightness', level: 'watch' },
    { id: 'w9', label: 'Bleeding continues even if light', level: 'watch' },
  ],
  partner: [
    { id: 'p1', text: 'Choose the hospital together and go on a tour' },
    { id: 'p2', text: 'Declare that you will take over household chores' },
    { id: 'p3', text: 'Make time to write the pregnancy record together' },
    { id: 'p4', text: 'Support meals during morning sickness' },
    { id: 'p5', text: 'Take over evening errands and shopping' },
    { id: 'p6', text: 'Confirm your maternity leave plan with your company' },
    { id: 'p7', text: 'Encourage rest without pushing her too hard' },
    { id: 'p8', text: 'Thoroughly prevent secondhand smoke at home' },
  ],
  tips: [
    {
      id: 't1',
      title: 'Morning sickness is more like "eating" than motion sickness',
      body: 'An empty stomach often makes it worse. Keeping small snacks available can make you feel better.',
    },
    {
      id: 't2',
      title: 'Hospital choice: "ease of access" matters',
      body: 'Look beyond equipment: check emergency contact, night availability, and staff atmosphere.',
    },
    {
      id: 't3',
      title: 'Make a habit of recording medical expenses',
      body: 'Keep receipts in the maternity handbook pocket. Useful for estimating childbirth costs and preparing deductions.',
    },
    {
      id: 't4',
      title: 'Report to your workplace with mental leeway',
      body: 'Even before the stable period, telling your supervisor when you feel stable makes later adjustments easier.',
    },
    {
      id: 't5',
      title: 'Morning sickness tips: cold, small, frequent hydration',
      body: 'Hydrate frequently with water, tea, or unsweetened carbonated water. Chilled dishes may be easier to eat.',
    },
    {
      id: 't6',
      title: 'Meal basics are one soup and three dishes',
      body: 'Combine staple, main, side, and soup. Aim for salt under 6.5 g per day. Keep folic acid, iron, and calcium in mind.',
    },
    {
      id: 't7',
      title: 'No smoking and no alcohol includes secondhand smoke',
      body: 'Avoid heated tobacco too. Make a rule never to smoke in the car or in front of family.',
    },
    {
      id: 't8',
      title: 'Do not self-medicate with over-the-counter drugs',
      body: 'During pregnancy and breastfeeding, always consult your OB/GYN, doctor, or pharmacist.',
    },
  ],
  adminGuide: [
    {
      id: 'a1',
      title: 'Issuance of the maternity handbook',
      body: 'Apply at the city/ward office of your residence. Once pregnancy is confirmed, receive it early and also check the prenatal checkup vouchers included.',
      when: 'As soon as pregnancy is confirmed',
    },
    {
      id: 'a2',
      title: 'Overview of the childbirth lump-sum allowance',
      body: 'If enrolled in health insurance, a benefit is paid per birth. Since October 2023, direct payment to childbirth costs (Obstetric Medical Compensation System) has started.',
      when: 'Procedures around childbirth',
    },
    {
      id: 'a3',
      title: 'Prenatal checkup subsidy vouchers',
      body: 'Checkup vouchers attached to the maternity handbook make a set number of prenatal checkups more affordable. Number and amount vary by municipality.',
      when: 'At maternity handbook issuance',
    },
    {
      id: 'a4',
      title: 'Maternity and childcare leave procedures',
      body: 'Maternity leave can start 6 weeks before the due date (14 weeks for multiples). Childcare leave can start 8 weeks after birth. Check employment insurance requirements too.',
      when: 'Apply to the company from mid to late pregnancy',
    },
    {
      id: 'a5',
      title: 'Medical expense deduction and high-cost medical care',
      body: 'If annual medical expenses exceed 100,000 yen, they may be deductible in your tax return. Always keep receipts.',
      when: 'Tax filing the year after childbirth',
    },
    {
      id: 'a6',
      title: 'Application for medical expense subsidy for pregnant and postpartum women',
      body: 'For pregnant/postpartum women residing in Niigata City, the amount excluding the co-payment for medical expenses is subsidized. Apply early because the subsidy period starts from the eligibility determination date.',
      when: 'Immediately after maternity handbook issuance',
    },
    {
      id: 'a7',
      title: 'Pregnancy support benefit (first time)',
      body: 'Targeted at pregnant women who submitted a pregnancy notification in Niigata City. Apply electronically within two years from the date fetal heartbeat was confirmed.',
      when: 'Early pregnancy onward',
    },
    {
      id: 'a8',
      title: 'National Health Insurance and National Pension pre/postnatal exemption',
      body: 'Insurance premiums are exempted or reduced during the pre/postnatal period. Pension benefits are not reduced. Applications can be submitted from six months before the due date.',
      when: 'Mid-pregnancy to after childbirth',
    },
  ],
};

export const pregnancy2En: Stage = {
  id: 'pregnancy-2',
  key: '2',
  title: 'Mid Pregnancy',
  subtitle: 'Make the stable period your ally',
  weeks: '14–27 weeks',
  description:
    'Many people find morning sickness settles. This is a golden time for preparation. Advance shopping and procedures, and keep your body moving.',
  mindCards: [
    {
      id: 'm1',
      title: 'Make the stable period your ally',
      body: 'Move your body within a comfortable range and regain your rhythm.',
      type: 'body',
    },
    {
      id: 'm2',
      title: 'Get organized',
      body: 'Feel the baby\'s growth and proceed with shopping and procedures in a planned way.',
      type: 'mind',
    },
    {
      id: 'm3',
      title: 'Enjoy shopping',
      body: 'Make a baby-item list and gather the necessities first.',
      type: 'life',
    },
    {
      id: 'm4',
      title: 'Talk about postpartum roles',
      body: 'Choose items together with your partner and discuss postpartum division of roles.',
      type: 'mind',
    },
    {
      id: 'm5',
      title: 'Feel the fetal movements',
      body: 'Noticing small movements makes it more real. If they suddenly decrease, consult your doctor.',
      type: 'body',
    },
    {
      id: 'm6',
      title: 'Coordinate with your workplace',
      body: 'Use the stable period as a chance to start discussing handover before maternity leave.',
      type: 'partner',
    },
    {
      id: 'm7',
      title: 'Keep iron and calcium in mind',
      body: 'Get iron from red meat, fish, and komatsuna; calcium from milk, yogurt, and komatsuna.',
      type: 'body',
    },
    {
      id: 'm8',
      title: 'Arrange postpartum support in advance',
      body: 'Consider postpartum care services, family support, and housekeeping help.',
      type: 'life',
    },
  ],
  checklist: [
    { id: 'c1', text: 'Confirm the schedule for prenatal checkups', category: 'hospital', note: 'During mid-pregnancy, roughly once every 4 weeks is common.' },
    { id: 'c2', text: 'Make a prioritized list of baby items', category: 'shopping', note: 'Classify into must/should/nice.' },
    { id: 'c3', text: 'Research baby clothing sizes', category: 'shopping', note: 'Mainly newborn to 50–60 cm.' },
    { id: 'c4', text: 'Plan the baby room or sleeping place', category: 'life', note: 'Consider whether to place the baby in the bedroom or living room.' },
    { id: 'c5', text: 'Confirm the due date and report to your workplace', category: 'admin', note: 'Submit documents according to company rules.' },
    { id: 'c6', text: 'Check the high-cost medical care system of your health insurance', category: 'admin', note: 'Consider obtaining a limit application certificate.' },
    { id: 'c7', text: 'Decide whether to return to your hometown for delivery or stay at the hospital', category: 'admin', note: 'Include transportation and family support.' },
    { id: 'c8', text: 'Check arrangements for pets, visitors, etc.', category: 'life', note: 'Imagine the environment after discharge too.' },
    { id: 'c9', text: 'Start a maternity photo or diary', category: 'life', note: 'It is fine to record even if not every day.' },
    { id: 'c10', text: 'Ensure time to move your body (follow hospital guidance)', category: 'body', note: 'Gentle exercise and stretching without overdoing it.' },
    { id: 'c11', text: 'Watch meal balance and weight gain', category: 'body', note: 'Use the recommended weight gain according to your BMI as a guideline (varies by individual).' },
    { id: 'c12', text: 'Confirm postpartum support with your partner', category: 'partner', note: 'Nighttime, household chores, and shopping division.' },
    { id: 'c13', text: 'Look for books or classes about childbirth', category: 'mind', note: 'Classes for couples are recommended.' },
    { id: 'c14', text: 'Prepare maternity wear', category: 'shopping', note: 'Choose materials gentle on the belly.' },
    { id: 'c15', text: 'Compare strollers and child seats', category: 'shopping', note: 'Check actual products in stores.' },
    { id: 'c16', text: 'Confirm estimated childbirth costs with the hospital', category: 'admin', note: 'Ask about private rooms and optional services.' },
    { id: 'c17', text: 'Research baby bottles and breastfeeding-related items', category: 'shopping', note: 'Learn about breastfeeding and pumping options.' },
    { id: 'c18', text: 'Wear a maternity mark', category: 'life', note: 'Use on trains and in facilities.' },
    { id: 'c19', text: 'Notice changes in fetal movements', category: 'body', note: 'Many people start feeling them around 18–22 weeks.' },
    { id: 'c20', text: 'Exchange information with friends and experienced moms', category: 'mind', note: 'Talking with people in the same period is reassuring.' },
    { id: 'c21', text: 'Be conscious of work handover', category: 'admin', note: 'Organize priorities and responsible persons.' },
    { id: 'c22', text: 'Discuss postpartum support with family', category: 'partner', note: 'Consider parents\' home or helper support.' },
    { id: 'c23', text: 'Decide the childbirth facility and make a delivery reservation', category: 'hospital', note: 'Including hometown delivery, early reservations are reassuring.' },
    { id: 'c24', text: 'Receive a pregnant-woman dental checkup', category: 'hospital', note: 'Around 16–30 weeks is a guideline. Cavities and periodontal disease increase preterm risk.' },
    { id: 'c25', text: 'Start full preparation of baby items', category: 'shopping', note: 'Focus on short/long undershirts 4–5 each, bedding, diapers, and bathing items.' },
    { id: 'c26', text: 'Consider using postpartum care services (overnight, day, or visit)', category: 'admin', note: 'Example: Niigata City. In principle, apply by 10 days before use from the 8th month of pregnancy.' },
    { id: 'c27', text: 'Apply for National Health Insurance and National Pension pre/postnatal exemption', category: 'admin', note: 'Applications can be submitted from six months before the due date. Pension benefits will not be reduced.' },
    { id: 'c28', text: 'Start moderate exercise (with doctor\'s permission)', category: 'body', note: 'Walking, maternity yoga, swimming, etc.' },
    { id: 'c29', text: 'Be careful with fish high in mercury', category: 'body', note: 'Salmon, horse mackerel, mackerel, and canned tuna are fine. Limit bigeye tuna, swordfish, southern bluefin tuna, and alfonsino.' },
    { id: 'c30', text: 'Confirm the guideline for weight gain', category: 'body', note: 'Use the recommended weight gain according to your BMI as a general guideline.' },
    { id: 'c31', text: 'Research childcare facilities and child-rearing support', category: 'admin', note: 'With returning to work in mind, also check Family Support Centers (example: Niigata City).' },
    { id: 'c32', text: 'Visualize household chore division with your partner', category: 'partner', note: 'Share garbage, laundry, and appliance use from during pregnancy.' },
  ],
  shopping: [
    {
      id: 's1',
      name: 'Maternity wear',
      priority: 'should',
      timing: 'From mid-pregnancy',
      priceYenMin: 3000,
      priceYenMax: 10000,
      note: 'Choose stretchy materials.',
    },
    {
      id: 's2',
      name: 'Baby undershirts (short and long)',
      priority: 'must',
      timing: 'From mid-pregnancy',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Newborn size 50–60 cm, 4–5 each.',
    },
    {
      id: 's3',
      name: 'Small set of diapers (newborn size)',
      priority: 'must',
      timing: 'From late pregnancy',
      priceYenMin: 1500,
      priceYenMax: 5000,
      note: 'Try a small amount before buying in bulk.',
    },
    {
      id: 's4',
      name: 'Baby moisturizer and bath soap',
      priority: 'should',
      timing: 'From mid-pregnancy',
      priceYenMin: 1500,
      priceYenMax: 4000,
      note: 'Check low-irritation and additive-free.',
    },
    {
      id: 's5',
      name: 'Nursing bra',
      priority: 'should',
      timing: 'From late pregnancy',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Prioritize comfort.',
    },
    {
      id: 's6',
      name: 'Body pillow',
      priority: 'nice',
      timing: 'From mid-pregnancy',
      priceYenMin: 3000,
      priceYenMax: 10000,
      note: 'Supports side sleeping.',
    },
    {
      id: 's7',
      name: 'Stroller',
      priority: 'should',
      timing: 'Mid-pregnancy onward',
      priceYenMin: 20000,
      priceYenMax: 80000,
      note: 'We recommend trying actual products in stores.',
    },
    {
      id: 's8',
      name: 'Child seat (for 0-year-olds)',
      priority: 'must',
      timing: 'Late pregnancy',
      priceYenMin: 20000,
      priceYenMax: 50000,
      note: 'Choose a type usable from newborn. Needed at discharge.',
    },
    {
      id: 's9',
      name: 'Baby futon or crib',
      priority: 'must',
      timing: 'Late pregnancy',
      priceYenMin: 15000,
      priceYenMax: 60000,
      note: 'Decide co-sleeping or bed. Rental is also an option if usage is short.',
    },
    {
      id: 's10',
      name: 'Nursing cushion',
      priority: 'should',
      timing: 'Late pregnancy to postpartum',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Reduces postpartum back strain.',
    },
    {
      id: 's11',
      name: 'Baby carrier',
      priority: 'should',
      timing: 'Late pregnancy to postpartum',
      priceYenMin: 3000,
      priceYenMax: 15000,
      note: 'Check newborn compatibility.',
    },
    {
      id: 's12',
      name: 'Postpartum underwear',
      priority: 'must',
      timing: 'Late pregnancy',
      priceYenMin: 2000,
      priceYenMax: 5000,
      note: '3–5 larger postpartum sizes.',
    },
    {
      id: 's13',
      name: 'Baby bath and bathing set',
      priority: 'should',
      timing: 'Mid to late pregnancy',
      priceYenMin: 2000,
      priceYenMax: 8000,
      note: 'Include thermometer, towels, and mild detergent.',
    },
    {
      id: 's14',
      name: 'Maternity or nursing bra',
      priority: 'should',
      timing: 'From mid-pregnancy',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Prioritize comfort. Choose a design usable after birth.',
    },
  ],
  warnings: [
    { id: 'w1', label: 'Regular abdominal pain or bleeding', level: 'urgent' },
    { id: 'w2', label: 'Possible water breaking', level: 'urgent' },
    { id: 'w3', label: 'Headache or vision changes', level: 'urgent' },
    { id: 'w4', label: 'Numbness in hands', level: 'watch' },
    { id: 'w5', label: 'Swelling in legs', level: 'watch' },
    { id: 'w6', label: 'Sudden rapid weight gain', level: 'watch' },
    { id: 'w7', label: 'Decreased fetal movement', level: 'watch' },
    { id: 'w8', label: 'Severe palpitations or shortness of breath', level: 'watch' },
    { id: 'w9', label: 'Urinary leakage or pain', level: 'watch' },
    { id: 'w10', label: 'Eye flickering or strong headache', level: 'watch' },
    { id: 'w11', label: 'Sudden sharp decrease in fetal movement', level: 'watch' },
  ],
  partner: [
    { id: 'p1', text: 'Go together to buy baby items' },
    { id: 'p2', text: 'List out postpartum role divisions' },
    { id: 'p3', text: 'Accompany the hospital tour' },
    { id: 'p4', text: 'Help with maternity photos' },
    { id: 'p5', text: 'Take over evening chores' },
    { id: 'p6', text: 'Prepare for childbirth payments' },
    { id: 'p7', text: 'Learn together from childcare books and classes' },
    { id: 'p8', text: 'Check mama\'s condition daily' },
    { id: 'p9', text: 'Practice postpartum chore division from pregnancy' },
  ],
  tips: [
    {
      id: 't1',
      title: 'Do not overdo it just because it is the stable period',
      body: 'Even if morning sickness settles, your body is changing greatly. Plan travel and moves carefully and within a comfortable range.',
    },
    {
      id: 't2',
      title: 'Start baby items from "must"',
      body: 'Cute items are tempting. First, gather essentials: undershirts, diapers, futon, child seat.',
    },
    {
      id: 't3',
      title: 'Fetal movement varies greatly',
      body: 'First-time mothers may feel it after 20 weeks. If it suddenly decreases or stops, contact the hospital.',
    },
    {
      id: 't4',
      title: 'Prepare documents for workplace reporting',
      body: 'Maternity and childcare leave applications and a medical certificate may be needed. Confirm required documents with HR to be safe.',
    },
    {
      id: 't5',
      title: 'Weight gain depends on BMI',
      body: 'Mid-pregnancy +250 kcal, late pregnancy and breastfeeding +450 kcal as a guideline. However, follow the recommended weight gain by BMI.',
    },
    {
      id: 't6',
      title: 'Keep iron and calcium in mind',
      body: 'Pregnancy tends to cause anemia, and calcium is needed for fetal bones and teeth. Get them from red meat, fish, komatsuna, milk, and yogurt.',
    },
    {
      id: 't7',
      title: 'Get a pregnant-woman dental checkup during the stable period',
      body: 'One free visit is available around 16–30 weeks. Periodontal disease increases preterm and low-birth-weight risk, so visit early.',
    },
    {
      id: 't8',
      title: 'Consider postpartum care early',
      body: 'Niigata City offers overnight, day, and visiting postpartum care. Applications are generally possible from the 8th month of pregnancy.',
    },
  ],
  adminGuide: [
    {
      id: 'a1',
      title: 'Application for maternity leave',
      body: 'Maternity leave can be taken from 6 weeks before the due date (14 weeks for multiples). Submit the application and medical certificate according to company rules.',
      when: 'Mid to late pregnancy',
    },
    {
      id: 'a2',
      title: 'High-cost medical care system',
      body: 'If monthly medical expenses become high, the amount exceeding the health insurance limit is refunded. Obtaining a limit application certificate in advance reduces the burden at the counter.',
      when: 'After mid-pregnancy when checkups increase',
    },
    {
      id: 'a3',
      title: 'Baby item distribution services',
      body: 'Local governments and health centers may distribute free or subsidized baby clothes, diapers, and formula. Check municipal websites for prenatal and postnatal support.',
      when: 'Check anytime',
    },
    {
      id: 'a4',
      title: 'Hometown delivery procedures',
      body: 'If receiving prenatal checkups or giving birth in the municipality where your parents live, moving-out notices and health insurance procedures may be needed. A referral letter may also be needed.',
      when: 'Once hometown delivery is decided',
    },
    {
      id: 'a5',
      title: 'Childcare leave benefits',
      body: 'During childcare leave, benefits are paid from employment insurance. Check the duration and wage conditions, and coordinate with your company.',
      when: 'Plan before maternity leave',
    },
    {
      id: 'a6',
      title: 'Deciding the childbirth facility and making a delivery reservation',
      body: 'Including hometown delivery, decide the hospital early and make a reservation. Popular facilities fill up quickly.',
      when: 'By early mid-pregnancy',
    },
    {
      id: 'a7',
      title: 'Pregnant-woman dental checkup',
      body: 'During pregnancy, cavities and periodontal disease tend to progress and increase preterm risk. Women who received the maternity handbook can receive one free checkup.',
      when: 'Around 16–30 weeks',
    },
    {
      id: 'a8',
      title: 'National Health Insurance and National Pension pre/postnatal exemption',
      body: 'Insurance premiums are exempted or reduced during the pre/postnatal period. Pension benefits are not reduced. Applications can be submitted from six months before the due date.',
      when: 'Mid-pregnancy to after childbirth',
    },
    {
      id: 'a9',
      title: 'Application for postpartum care services',
      body: 'Niigata City offers overnight, day, and visiting postpartum care. In principle, apply by 10 days before the use date from the 8th month of pregnancy (varies by municipality).',
      when: 'Mid to late pregnancy',
    },
    {
      id: 'a10',
      title: 'Application for medical expense subsidy for pregnant and postpartum women',
      body: 'For pregnant/postpartum women residing in Niigata City, the amount excluding the co-payment is subsidized. Apply early because the subsidy period starts from the eligibility determination date.',
      when: 'Immediately after maternity handbook issuance',
    },
  ],
};

export const pregnancy3En: Stage = {
  id: 'pregnancy-3',
  key: '3',
  title: 'Late Pregnancy',
  subtitle: 'Prepare and take care of your body',
  weeks: '28 weeks onward',
  description: 'Your belly grows and childbirth approaches. Firm up hospital admission preparation and your postpartum support network.',
  mindCards: [
    { id: 'm1', title: 'Reduce anxiety with preparation', body: 'Filling out lists can increase your mental leeway.', type: 'mind' },
    { id: 'm2', title: 'Take care of your body', body: 'Avoid standing work and long travel, and prioritize rest.', type: 'body' },
    { id: 'm3', title: 'How to tell labor and water breaking', body: 'Confirm contact numbers and timing so you can stay calm.', type: 'life' },
    { id: 'm4', title: 'Arrange postpartum support', body: 'Reserve household and childcare support to protect mama.', type: 'mind' },
    { id: 'm5', title: 'Choose a pediatrician', body: 'Check nearby pediatricians and night/ holiday availability to prepare for sudden fever.', type: 'life' },
    { id: 'm6', title: 'Clarify the partner\'s role', body: 'Birth presence, household chores, and contact person. Decide concrete roles.', type: 'partner' },
    { id: 'm7', title: 'Keep weight management until the end', body: 'Use the recommended weight gain by BMI as a guideline, and keep +450 kcal in mind for late pregnancy.', type: 'body' },
    { id: 'm8', title: 'Image of breastfeeding and pumping', body: 'Including combining breast milk and formula, prepare without rushing.', type: 'mind' },
  ],
  checklist: [
    { id: 'c1', text: 'Prepare a hospital bag', category: 'shopping', note: 'Maternity handbook, insurance card, clothes, hygiene items.' },
    { id: 'c2', text: 'Confirm the hospital admission schedule', category: 'hospital', note: 'Admission method, items to bring, visiting hours.' },
    { id: 'c3', text: 'Register a labor taxi and contact number', category: 'life', note: 'Confirm companies that respond at night.' },
    { id: 'c4', text: 'Share emergency contacts with family', category: 'admin', note: 'Hospital, taxi, parents\' home, etc.' },
    { id: 'c5', text: 'Confirm the newborn home-visit checkup reservation', category: 'hospital', note: 'Municipal visit and one-month checkup.' },
    { id: 'c6', text: 'Arrange postpartum help', category: 'life', note: 'Parents\' home, helper, postpartum care services.' },
    { id: 'c7', text: 'Buy the last needed baby items', category: 'shopping', note: 'Confirm that essentials are ready.' },
    { id: 'c8', text: 'Prepare childbirth payment', category: 'admin', note: 'Confirm cash, card, and transfer options.' },
    { id: 'c9', text: 'Confirm the childbirth lump-sum allowance application procedure', category: 'admin', note: 'Direct payment through the hospital or self-application.' },
    { id: 'c10', text: 'Plan hometown return or travel', category: 'admin', note: 'Transportation and emergency response.' },
    { id: 'c11', text: 'Confirm how to distinguish labor and water breaking', category: 'body', note: 'Decide when to contact the hospital.' },
    { id: 'c12', text: 'Confirm father\'s maternity leave and birth presence', category: 'partner', note: 'Hospital rules and company procedures.' },
    { id: 'c13', text: 'Decide who handles nighttime contact and response', category: 'partner', note: 'Who to contact when labor starts.' },
    { id: 'c14', text: 'Observe fetal movements and do not miss abnormalities', category: 'body', note: 'Use a fetal movement card.' },
    { id: 'c15', text: 'Choose a pediatrician and confirm hours', category: 'hospital', note: 'Confirm night and holiday availability.' },
    { id: 'c16', text: 'Confirm how to write the birth notification', category: 'admin', note: 'Submit within 14 days after birth.' },
    { id: 'c17', text: 'Wash baby clothes', category: 'life', note: 'Use neutral detergent for newborn clothes.' },
    { id: 'c18', text: 'Confirm child seat installation', category: 'shopping', note: 'Prepare for use at discharge.' },
    { id: 'c19', text: 'Consider postpartum meal menus', category: 'life', note: 'List make-ahead dishes and side dishes.' },
    { id: 'c20', text: 'Ensure time to relax', category: 'mind', note: 'Relax with massage or music.' },
    { id: 'c21', text: 'Review the birth plan with your partner', category: 'partner', note: 'Response during labor, presence at birth.' },
    { id: 'c22', text: 'Confirm wishes regarding painless delivery or delivery method', category: 'hospital', note: 'Confirm whether the hospital handles it.' },
    { id: 'c23', text: 'Confirm labor taxi registration and night availability', category: 'life', note: 'Confirm early morning and late night response, and fee structure.' },
    { id: 'c24', text: 'Confirm pediatrician night and holiday availability', category: 'hospital', note: 'Decide a regular pediatrician early.' },
    { id: 'c25', text: 'Apply for father\'s maternity leave (childbirth childcare leave)', category: 'partner', note: 'Can be taken up to 4 weeks within 8 weeks after birth. Notify the company by 2 weeks before the start date.' },
    { id: 'c26', text: 'Check the second pregnancy support benefit application', category: 'admin', note: 'Within two years from 8 weeks before the due date, etc. Example: Niigata City.' },
    { id: 'c27', text: 'Apply for postpartum care services', category: 'admin', note: 'Example: Niigata City. In principle, apply by 10 days before the date from the 8th month. Overnight, day, and visiting options available.' },
    { id: 'c28', text: 'Confirm moving-out procedures for hometown delivery', category: 'admin', note: 'Health insurance and resident record transfers may be needed.' },
    { id: 'c29', text: 'Confirm payment methods with the hospital', category: 'admin', note: 'Cash, card, transfer, or direct payment (childbirth lump-sum allowance).' },
    { id: 'c30', text: 'Do a final check of the hospital bag list', category: 'shopping', note: 'Some hospitals provide items, so confirm in advance.' },
    { id: 'c31', text: 'Decide rules against secondhand smoke at home', category: 'life', note: 'No smoking near pregnant women or infants.' },
  ],
  shopping: [
    { id: 's1', name: 'Hospital bag set', priority: 'must', timing: 'Late pregnancy', priceYenMin: 5000, priceYenMax: 15000, note: 'Maternity handbook, insurance card, postpartum pads, front-opening pajamas, toiletries, etc.' },
    { id: 's2', name: 'Postpartum underwear', priority: 'must', timing: 'Late pregnancy', priceYenMin: 2000, priceYenMax: 5000, note: '3–5 larger postpartum sizes.' },
    { id: 's3', name: 'Breast pads', priority: 'must', timing: 'Late pregnancy', priceYenMin: 1000, priceYenMax: 3000, note: 'Disposable or cloth.' },
    { id: 's4', name: 'Baby clothes (including discharge outfit)', priority: 'must', timing: 'Late pregnancy', priceYenMin: 3000, priceYenMax: 10000, note: 'Prepare about 4–5 newborn and 50–60 cm sizes each.' },
    { id: 's5', name: 'Baby futon or crib', priority: 'must', timing: 'Late pregnancy', priceYenMin: 15000, priceYenMax: 60000, note: 'Choose co-sleeping goods or a bed. Rental is also an option.' },
    { id: 's6', name: 'Baby carrier', priority: 'should', timing: 'Late pregnancy to postpartum', priceYenMin: 3000, priceYenMax: 15000, note: 'Check newborn compatibility.' },
    { id: 's7', name: 'Nursing cushion', priority: 'should', timing: 'Late pregnancy to postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Reduces back strain during breastfeeding.' },
    { id: 's8', name: 'Child seat (for 0-year-olds)', priority: 'must', timing: 'Late pregnancy', priceYenMin: 20000, priceYenMax: 50000, note: 'Needed at discharge. Also confirm installation.' },
    { id: 's9', name: 'Large quantity of newborn diapers', priority: 'must', timing: 'Late pregnancy', priceYenMin: 4000, priceYenMax: 12000, note: 'Assume more than 10 per day.' },
    { id: 's10', name: 'Baby wipes (large pack)', priority: 'must', timing: 'Late pregnancy', priceYenMin: 1000, priceYenMax: 3000, note: 'Low-irritation type for newborns.' },
    { id: 's11', name: 'Baby bottle and nipple set', priority: 'should', timing: 'Late pregnancy to postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Prepare for both breast milk and formula.' },
    { id: 's12', name: 'Baby thermometer', priority: 'should', timing: 'Late pregnancy', priceYenMin: 2000, priceYenMax: 5000, note: 'Choose ear, non-contact, or armpit type.' },
    { id: 's13', name: 'Nursing bra (including for hospital stay)', priority: 'must', timing: 'Late pregnancy', priceYenMin: 2000, priceYenMax: 6000, note: 'Prioritize front-opening and comfort.' },
    { id: 's14', name: 'Postpartum pads and maternity pads', priority: 'must', timing: 'Late pregnancy', priceYenMin: 1500, priceYenMax: 5000, note: 'Enough for hospital stay and 2–3 weeks postpartum.' },
    { id: 's15', name: 'Labor taxi registration (estimated fee)', priority: 'should', timing: 'Late pregnancy', priceYenMin: 0, priceYenMax: 5000, note: 'Varies by municipality and taxi company. Pre-registration may be required.' },
  ],
  warnings: [
    { id: 'w1', label: 'Water breaking', level: 'urgent' },
    { id: 'w2', label: 'Bleeding', level: 'urgent' },
    { id: 'w3', label: 'Labor pains', level: 'urgent' },
    { id: 'w4', label: 'Severe headache or blurred vision', level: 'urgent' },
    { id: 'w5', label: 'Feeling drowsy or losing consciousness', level: 'urgent' },
    { id: 'w6', label: 'Decreased fetal movement', level: 'watch' },
    { id: 'w7', label: 'Swelling in legs', level: 'watch' },
    { id: 'w8', label: 'Numbness in hands', level: 'watch' },
    { id: 'w9', label: 'Unable to sleep', level: 'watch' },
    { id: 'w10', label: 'Persistent vomiting or diarrhea', level: 'watch' },
    { id: 'w11', label: 'Extremely decreased fetal movement', level: 'watch' },
    { id: 'w12', label: 'Sudden increase in swelling of hands or face', level: 'watch' },
  ],
  partner: [
    { id: 'p1', text: 'Check the contents of the hospital bag' },
    { id: 'p2', text: 'Decide who is the contact person during labor' },
    { id: 'p3', text: 'Do a final check of postpartum household and childcare division' },
    { id: 'p4', text: 'Confirm child seat installation' },
    { id: 'p5', text: 'Research pediatrician candidates together' },
    { id: 'p6', text: 'Confirm wishes on the birth plan' },
    { id: 'p7', text: 'Confirm transportation for hometown return' },
    { id: 'p8', text: 'Encourage mama to prioritize rest' },
    { id: 'p9', text: 'Notify the company about postpartum papa childcare leave' },
  ],
  tips: [
    { id: 't1', title: 'Complete the hospital bag by 34–36 weeks', body: 'Prepare for sudden admission by packing the maternity handbook, insurance card, postpartum pads, and a change of clothes. It helps to show the contents to your partner.' },
    { id: 't2', title: 'Registering a labor taxi gives peace of mind', body: 'Confirm night and early morning availability, and save the contact number in your phone. Even if unused, knowing it helps you relax.' },
    { id: 't3', title: 'Contact the hospital immediately if fetal movement decreases', body: 'If fetal movement is clearly less than usual, call the hospital. Do not hesitate; consulting quickly is important.' },
    { id: 't4', title: 'Postpartum household chores do not need to be perfect', body: 'Keep cleaning and laundry to a minimum. Use ready-made meals and side dishes. Mama\'s recovery is the top priority postpartum.' },
    { id: 't5', title: 'Check pediatrician night and holiday availability', body: 'Prepare for sudden fever by making a regular pediatrician list and a hospital available nights/holidays.' },
    { id: 't6', title: 'Do not forget procedures for hometown delivery', body: 'If receiving checkups or giving birth where your parents live, health insurance and resident record procedures may be needed.' },
    { id: 't7', title: 'Apply for postpartum care from the 8th month', body: 'Niigata City offers overnight, day, and visiting postpartum care. Apply by 10 days before the date, so plan early.' },
    { id: 't8', title: 'Thoroughly prevent secondhand smoke', body: 'Make a rule not to smoke near pregnant women or infants. Ventilation fans do not prevent sidestream smoke.' },
  ],
  adminGuide: [
    { id: 'a1', title: 'Submission of birth notification', body: 'Submit to the city/ward office of the father or mother\'s residence or permanent domicile within 14 days after the baby\'s birth. The hospital will provide the required documents.', when: 'Within 14 days after birth' },
    { id: 'a2', title: 'Claim for childbirth lump-sum allowance', body: 'If enrolled in health insurance, a benefit is paid per birth. Either direct payment through the hospital or self-application is possible.', when: 'At childbirth or after' },
    { id: 'a3', title: 'Infant medical certificate', body: 'After submitting the birth notification, you will receive guidance on applying for infant medical expense subsidies. Present it at pediatric visits.', when: 'After birth notification submission' },
    { id: 'a4', title: 'Reservation for postpartum care services', body: 'Municipalities and hospitals provide overnight or visiting postpartum care. Receive rest and childcare support. Example: Niigata City.', when: 'Late pregnancy to shortly after birth' },
    { id: 'a5', title: 'Father\'s maternity leave', body: 'Childbirth childcare leave can be taken within 8 weeks after birth. Benefits are paid from employment insurance. Notify the company in advance.', when: 'Apply to the company in late pregnancy' },
    { id: 'a6', title: 'Moving-out procedures for hometown delivery', body: 'If giving birth or receiving checkups where your parents live, health insurance and resident record transfers may be needed. Confirm in advance.', when: 'Adjust before childbirth' },
    { id: 'a7', title: 'Pregnancy support benefit (second time)', body: 'For mothers who give birth in Niigata City. Apply electronically within two years from 8 weeks before the due date, etc.', when: 'Before and after childbirth' },
    { id: 'a8', title: 'Application for postpartum care services', body: 'Niigata City offers overnight, day, and visiting care. In principle, apply by 10 days before the date from the 8th month of pregnancy.', when: 'Late pregnancy' },
    { id: 'a9', title: 'National Health Insurance and National Pension pre/postnatal exemption', body: 'Insurance premiums are exempted or reduced during the pre/postnatal period. Pension benefits are not reduced. Applications can be submitted from six months before the due date.', when: 'Late pregnancy to after childbirth' },
    { id: 'a10', title: 'Children\'s medical telephone consultation (#8000)', body: 'In Niigata City and elsewhere, nurses are available for consultation on nights/holidays when you are unsure whether to seek medical care for your child. Varies by municipality.', when: 'Keep in mind anytime' },
  ],
};

export const birthEn: Stage = {
  id: 'birth',
  key: '4',
  title: 'Before and After Birth',
  subtitle: 'Face the unknown journey in your own way',
  weeks: 'Before and after birth',
  description: 'Childbirth is unpredictable. Do not forget important procedures, and breathe together with your partner.',
  mindCards: [
    { id: 'm1', title: 'Believe in yourself', body: 'Childbirth is an unknown journey. Trust your body and the medical staff, and let go.', type: 'mind' },
    { id: 'm2', title: 'Do not rush postpartum', body: 'Recovery takes time. Accumulate small moments of rest.', type: 'body' },
    { id: 'm3', title: 'Breathe with your partner', body: 'Welcome the new family together. Even simple words are a big support.', type: 'mind' },
    { id: 'm4', title: 'Imagine life after discharge', body: 'Prepare your home environment and a system to ask for support.', type: 'life' },
    { id: 'm5', title: 'Skinship with the baby', body: 'Through bathing and diaper changes, build parent-child trust little by little.', type: 'partner' },
    { id: 'm6', title: 'Proceed with procedures step by step', body: 'Advance the birth notification and various applications according to the hospital and municipality guidance.', type: 'life' },
    { id: 'm7', title: 'Do not rush breast milk or formula', body: 'It is okay if breast milk does not come right away; formula can supplement. Do not blame yourself.', type: 'mind' },
  ],
  checklist: [
    { id: 'c1', text: 'Complete hospital admission procedures', category: 'hospital', note: 'Insurance card, maternity handbook, consent forms, etc.' },
    { id: 'c2', text: 'Bring the birth plan', category: 'hospital', note: 'Response during labor and pain relief wishes.' },
    { id: 'c3', text: 'Confirm the partner birth presence consent form', category: 'hospital', note: 'Follow hospital rules.' },
    { id: 'c4', text: 'Organize discharge procedures and required documents', category: 'admin', note: 'Birth notification, medical certificate, checkup vouchers, etc.' },
    { id: 'c5', text: 'Confirm preparation and submission deadline for the birth notification', category: 'admin', note: 'Within 14 days after birth.' },
    { id: 'c6', text: 'Confirm issuance of the maternity handbook', category: 'admin', note: 'At the hospital or municipality.' },
    { id: 'c7', text: 'Carry out the childbirth lump-sum allowance application procedure', category: 'admin', note: 'Through the hospital or your health insurance union.' },
    { id: 'c8', text: 'Confirm the infant medical certificate procedure', category: 'admin', note: 'At the municipal children\'s section.' },
    { id: 'c9', text: 'Check the home environment (baby items, temperature)', category: 'life', note: 'Aim for room temperature 22–26°C and humidity 50–60%.' },
    { id: 'c10', text: 'Confirm breastfeeding preparation and method', category: 'body', note: 'Either breast milk or formula is okay.' },
    { id: 'c11', text: 'Confirm newborn bathing method', category: 'life', note: 'Follow the hospital guidance.' },
    { id: 'c12', text: 'Confirm nighttime support system', category: 'partner', note: 'Division of breastfeeding and diaper changes.' },
    { id: 'c13', text: 'List shopping for after discharge', category: 'shopping', note: 'Postpartum pads, diapers, formula, etc.' },
    { id: 'c14', text: 'Ensure time to watch mama\'s recovery', category: 'mind', note: 'Watch for changes in pain and bleeding.' },
    { id: 'c15', text: 'Observe the newborn\'s excretion and sleep rhythm', category: 'life', note: 'Record number of breast milk/formula feedings too.' },
    { id: 'c16', text: 'Prepare postpartum meals', category: 'body', note: 'Nutritional balance and hydration.' },
    { id: 'c17', text: 'Confirm checkup appointments for the baby', category: 'hospital', note: 'One-month checkup and vaccinations.' },
    { id: 'c18', text: 'Express gratitude to your partner', category: 'mind', note: 'Putting small support into words helps it continue.' },
    { id: 'c19', text: 'Submit the birth notification within 14 days', category: 'admin', note: 'To the city/ward office of the father or mother\'s residence or permanent domicile.' },
    { id: 'c20', text: 'Apply for child allowance', category: 'admin', note: 'For 0-year-olds, 15,000 yen per month until age 3 (income limits apply). Apply by the end of the birth month.' },
    { id: 'c21', text: 'Apply for child medical expense subsidy', category: 'admin', note: 'Eligible age and content vary by municipality. Apply after joining health insurance.' },
    { id: 'c22', text: 'Receive newborn hearing screening and congenital metabolic disorder screening', category: 'hospital', note: 'Hearing screening soon after birth; metabolic disorder screening by blood sample around 4–6 days.' },
    { id: 'c23', text: 'Use postpartum care services (if applied)', category: 'admin', note: 'Overnight, day, or visiting. Example: Niigata City.' },
    { id: 'c24', text: 'Do not rush combining breast milk and formula', category: 'body', note: 'If breast milk is insufficient, supplement with formula within a comfortable range.' },
    { id: 'c25', text: 'Take postpartum papa childcare leave', category: 'partner', note: 'Up to 4 weeks within 8 weeks after birth. Notify the company by 2 weeks before the start date.' },
    { id: 'c26', text: 'Confirm secondhand smoke prevention at home', category: 'life', note: 'No smoking near pregnant women or infants.' },
  ],
  shopping: [
    { id: 's1', name: 'Postpartum night bra', priority: 'must', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 6000, note: 'Design easy for breastfeeding.' },
    { id: 's2', name: 'Postpartum pads', priority: 'must', timing: 'Before and after birth', priceYenMin: 1500, priceYenMax: 5000, note: 'For hospital stay and 2–3 weeks postpartum.' },
    { id: 's3', name: 'Newborn diapers', priority: 'must', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 6000, note: 'Assume more than 10 per day.' },
    { id: 's4', name: 'Formula set (powdered milk and bottles)', priority: 'should', timing: 'Before and after birth', priceYenMin: 3000, priceYenMax: 10000, note: 'Prepare even if combining with breast milk.' },
    { id: 's5', name: 'Nursing cover', priority: 'should', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 5000, note: 'Convenient when out or receiving visitors.' },
    { id: 's6', name: 'Baby thermometer', priority: 'should', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 5000, note: 'Non-contact or ear type.' },
    { id: 's7', name: 'Baby wipes (large pack)', priority: 'must', timing: 'Before and after birth', priceYenMin: 1000, priceYenMax: 3000, note: 'Low-irritation type for newborns.' },
    { id: 's8', name: 'Baby bathing set', priority: 'should', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 6000, note: 'Thermometer, towels, soap, etc.' },
    { id: 's9', name: 'Nursing pillow', priority: 'nice', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 6000, note: 'Reduces back strain.' },
    { id: 's10', name: 'Hairband and slippers for mama', priority: 'nice', timing: 'Before and after birth', priceYenMin: 1000, priceYenMax: 3000, note: 'Increase comfort during hospital stay.' },
    { id: 's11', name: 'Breast pump (electric or manual)', priority: 'nice', timing: 'Before and after birth', priceYenMin: 3000, priceYenMax: 20000, note: 'Choose according to milk amount and lifestyle.' },
    { id: 's12', name: 'Baby nail clippers', priority: 'should', timing: 'Before and after birth', priceYenMin: 500, priceYenMax: 2000, note: 'Safety design for newborns.' },
    { id: 's13', name: 'Diaper pail', priority: 'nice', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 6000, note: 'Odor control.' },
    { id: 's14', name: 'Bottle sterilizer', priority: 'nice', timing: 'Before and after birth', priceYenMin: 2000, priceYenMax: 8000, note: 'Choose microwave, liquid, or boiling type.' },
  ],
  warnings: [
    { id: 'w1', label: 'Heavy bleeding', level: 'urgent' },
    { id: 'w2', label: 'Severe pain', level: 'urgent' },
    { id: 'w3', label: 'Loss of consciousness', level: 'urgent' },
    { id: 'w4', label: 'Difficulty breathing', level: 'urgent' },
    { id: 'w5', label: 'Fever', level: 'watch' },
    { id: 'w6', label: 'Wound pain', level: 'watch' },
    { id: 'w7', label: 'Breast engorgement', level: 'watch' },
    { id: 'w8', label: 'Baby is unresponsive', level: 'watch' },
    { id: 'w9', label: 'Unable to feed', level: 'watch' },
    { id: 'w10', label: 'Strong feelings of sadness', level: 'watch' },
    { id: 'w11', label: 'Strong or prolonged newborn jaundice', level: 'watch' },
    { id: 'w12', label: 'Umbilical bleeding or pus', level: 'watch' },
    { id: 'w13', label: 'Baby breathes fast or seems distressed', level: 'urgent' },
  ],
  partner: [
    { id: 'p1', text: 'Confirm what you can do during presence or waiting' },
    { id: 'p2', text: 'Prepare to take over household chores after discharge' },
    { id: 'p3', text: 'Encourage mama to prioritize rest' },
    { id: 'p4', text: 'Advance the birth notification and procedures together' },
    { id: 'p5', text: 'Support nighttime diaper changes and feedings' },
    { id: 'p6', text: 'Observe the baby and report to mama' },
    { id: 'p7', text: 'Confirm transportation at discharge' },
    { id: 'p8', text: 'Notify the company about postpartum papa childcare leave' },
  ],
  tips: [
    { id: 't1', title: 'Hold the birth plan as a "wish"', body: 'Labor and childbirth are unpredictable. Even if things do not go as planned, mama and baby\'s safety are the top priority. Be flexible.' },
    { id: 't2', title: 'Hydration and rest immediately after birth', body: 'Breastfeeding and recovery require fluids. Sleep when the baby sleeps. Housework can wait.' },
    { id: 't3', title: 'Do not rush breastfeeding', body: 'It is okay to supplement with formula if breast milk does not come right away. Consult midwives and breastfeeding clinics as you continue.' },
    { id: 't4', title: 'Diaper changes: safe and calm', body: 'At first it may feel awkward. Speak to the baby while doing it. Always wash hands and do not look away.' },
    { id: 't5', title: 'Combining breast milk and formula is okay', body: 'Do not be too fixated on breastfeeding. Put mama\'s stress and the baby\'s nutrition first. It is okay to use formula.' },
    { id: 't6', title: 'For sudden newborn illness, call #8000', body: 'On nights and holidays when you are unsure whether to seek care, you can consult nurses via the children\'s medical telephone consultation #8000 in Niigata City and other areas.' },
    { id: 't7', title: 'Prevent secondhand smoke', body: 'Including heated tobacco, make a rule not to smoke near pregnant women or infants. Ventilation fans do not prevent sidestream smoke.' },
    { id: 't8', title: 'Consult doctors about over-the-counter medicines', body: 'Do not stop chronic medicines on your own or take over-the-counter medicines. Always consult your OB/GYN, doctor, or pharmacist.' },
  ],
  adminGuide: [
    { id: 'a1', title: 'Submission of birth notification', body: 'Submit to the city/ward office of the father or mother\'s residence or permanent domicile within 14 days after birth. The hospital will provide the required documents.', when: 'Within 14 days after birth' },
    { id: 'a2', title: 'Claim for childbirth lump-sum allowance', body: 'If enrolled in health insurance, direct payment to childbirth costs or post-application is possible. The hospital will guide the procedure.', when: 'At childbirth or shortly after' },
    { id: 'a3', title: 'Issuance of infant medical certificate', body: 'After submitting the birth notification, the municipality will guide you on infant medical expense subsidy procedures. Present it at pediatric visits.', when: 'After birth notification submission' },
    { id: 'a4', title: 'Application for child allowance', body: 'For children up to junior high third year. For 0-year-olds, 15,000 yen per month until age 3 (income limits apply). Apply by the end of the birth month.', when: 'Immediately after birth' },
    { id: 'a5', title: 'Father\'s childbirth childcare leave', body: 'Can be taken within 8 weeks after birth. Benefits are paid from employment insurance. Notify the company in advance.', when: 'Within 8 weeks after birth' },
    { id: 'a6', title: 'Confirmation of maternity handbook issuance', body: 'Receive the maternity handbook at the hospital or municipality, and confirm the newborn checkup schedule.', when: 'Immediately after birth' },
    { id: 'a7', title: 'Newborn hearing screening and congenital metabolic disorder screening', body: 'Hearing screening is done soon after birth; metabolic disorder screening by blood sample around 4–6 days. Receive them for early detection.', when: 'Immediately after birth to about 1 week' },
    { id: 'a8', title: 'Use of postpartum care services', body: 'Niigata City offers overnight, day, and visiting postpartum care. Application may be needed in advance.', when: 'Immediately after birth to 6 months postpartum' },
    { id: 'a9', title: 'Application for child medical expense subsidy', body: 'Subsidizes the out-of-pocket portion of children\'s medical expenses (insured treatment). Varies by municipality. Apply after joining health insurance.', when: 'Promptly after birth' },
    { id: 'a10', title: 'Niigata Prefecture Komusubi fixed-term deposit', body: 'For children under 1 born in Niigata Prefecture. Apply through the prefecture\'s electronic application system by the day before the first birthday.', when: 'By the day before the first birthday' },
    { id: 'a11', title: 'Children\'s medical telephone consultation (#8000)', body: 'In Niigata City and elsewhere, nurses are available for consultation on nights/holidays when you are unsure whether to seek care for your child.', when: 'Emergency' },
  ],
  careScenes: [
    {
      id: 'cs1',
      title: 'Diaper change',
      items: [
        'Prepare diapers, wipes, and a waterproof sheet.',
        'Always wash hands and never look away.',
        'For girls, wipe from front to back.',
        'If diaper rash is a concern, apply a thin layer of moisturizer.',
        'Dispose of used diapers hygienically.',
      ],
    },
    {
      id: 'cs2',
      title: 'Breastfeeding and formula',
      items: [
        'For breast milk, roughly every 3 hours, on demand is okay.',
        'Make formula at the correct concentration and temperature.',
        'Sterilize bottles before and after use.',
        'Do not forget to burp the baby.',
        'After feeding, lay the baby on their side for about 30 minutes.',
      ],
    },
    {
      id: 'cs3',
      title: 'Bathing',
      items: [
        'Warm the room and gather supplies.',
        'Aim for bath temperature around 38–40°C.',
        'Gently wash the neck, armpits, and diaper area.',
        'Wipe the face separately with clean water.',
        'Dry and moisturize immediately after washing.',
      ],
    },
    {
      id: 'cs4',
      title: 'Going out',
      items: [
        'Reconfirm child seat installation.',
        'Bring extra diapers, wipes, and changes of clothes.',
        'Start with short outings.',
        'Choose facilities with nursing spaces.',
        'Avoid crowds and do not overdo it.',
      ],
    },
  ],
};

export const postpartumEarlyEn: Stage = {
  id: 'postpartum-early',
  key: '5',
  title: 'Postpartum 0–3 Months',
  subtitle: 'Days of welcoming a new life',
  weeks: '0–3 months postpartum',
  description: 'Breastfeeding and sleep rhythms are not yet settled. Take it easy and value mama\'s own recovery too.',
  mindCards: [
    { id: 'm1', title: 'Do not seek perfection', body: 'There is not one correct answer when welcoming a small life.', type: 'mind' },
    { id: 'm2', title: 'Get used to one thing at a time', body: 'Start pelvic floor and gentle exercise according to the hospital guidance.', type: 'body' },
    { id: 'm3', title: 'Start outings short', body: 'Value your own pace and go out within a comfortable range.', type: 'life' },
    { id: 'm4', title: 'Support each other at night', body: 'Support nighttime feedings and find ways to let mama rest.', type: 'mind' },
    { id: 'm5', title: 'Asking for help is a strength', body: 'Do not hold childcare worries alone; experts and municipalities are available for consultation.', type: 'mind' },
    { id: 'm6', title: 'Value couple time', body: 'Make even a short time to talk and continue adjusting as a couple.', type: 'partner' },
    { id: 'm7', title: 'Breastfeeding at mama\'s pace', body: 'If you feel breast milk is insufficient, supplement with formula. Protecting mama\'s physical and mental health is the top priority.', type: 'body' },
  ],
  checklist: [
    { id: 'c1', text: 'Submit the birth notification at home', category: 'admin', note: 'Within 14 days after birth.' },
    { id: 'c2', text: 'Confirm application for child allowance and special benefits', category: 'admin', note: 'Check guidance from the municipality.' },
    { id: 'c3', text: 'Confirm the one-month checkup and vaccination schedule', category: 'hospital', note: 'Record in the maternity handbook.' },
    { id: 'c4', text: 'Start recording feedings and sleep', category: 'life', note: 'App or notebook is okay.' },
    { id: 'c5', text: 'Start pelvic floor exercises (follow guidance)', category: 'body', note: 'Within a comfortable range.' },
    { id: 'c6', text: 'Schedule the 6-week postpartum health checkup', category: 'hospital', note: 'At the hospital or municipality.' },
    { id: 'c7', text: 'Confirm postpartum consultation windows (municipality, health center)', category: 'mind', note: 'Night telephone consultation may be available.' },
    { id: 'c8', text: 'Slow down the pace of housework', category: 'life', note: 'Keep cleaning to a minimum.' },
    { id: 'c9', text: 'Decide nighttime and weekend shifts with your partner', category: 'partner', note: 'Distribute rest evenly.' },
    { id: 'c10', text: 'Plan short outings', category: 'life', note: 'Start with about 1–2 hours.' },
    { id: 'c11', text: 'Decide who to consult about childcare worries', category: 'mind', note: 'Midwife, public health nurse, experienced mom.' },
    { id: 'c12', text: 'Organize baby daily items', category: 'life', note: 'Smooth flow for diapers and changes of clothes.' },
    { id: 'c13', text: 'Confirm breastfeeding posture and nipple care', category: 'body', note: 'Consult if pain continues.' },
    { id: 'c14', text: 'Prepare for the shrine visit', category: 'life', note: 'Clothes, schedule, amulet, etc.' },
    { id: 'c15', text: 'Make vaccination appointments', category: 'hospital', note: 'BCG, rotavirus, Hib, etc.' },
    { id: 'c16', text: 'Prioritize mama\'s meals and sleep', category: 'mind', note: 'Rest on the same rhythm as the baby.' },
    { id: 'c17', text: 'Put gratitude to your partner into words', category: 'partner', note: 'Even small things, say them.' },
    { id: 'c18', text: 'Plan additional purchases of childcare items', category: 'shopping', note: 'Diapers, wipes, etc.' },
    { id: 'c19', text: 'Apply for child medical expense subsidy', category: 'admin', note: 'Eligible age and content vary by municipality. Apply after joining health insurance.' },
    { id: 'c20', text: 'Apply for child allowance', category: 'admin', note: 'For 0-year-olds, 15,000 yen per month until age 3 (income limits apply). By the end of the birth month.' },
    { id: 'c21', text: 'Confirm the Niigata Prefecture Komusubi fixed-term deposit application', category: 'admin', note: 'For children under 1 born in Niigata Prefecture. Apply by the day before the first birthday.' },
    { id: 'c22', text: 'Receive the newborn home visit ("Hello Baby")', category: 'hospital', note: 'Guidance from the municipality. Use it for childcare and health consultation.' },
    { id: 'c23', text: 'Schedule the 6–8 week postpartum health checkup', category: 'hospital', note: 'Check uterine recovery, anemia, blood pressure, breasts, pelvic floor, etc.' },
    { id: 'c24', text: 'Maintain mama\'s oral care and postpartum nutrition', category: 'body', note: 'Keep iron and calcium in mind during breastfeeding. +450 kcal is a general guideline.' },
    { id: 'c25', text: 'Confirm consultation windows for childcare worries', category: 'mind', note: 'Niigata City Midwives Association "Hello Midwife" and each ward\'s Children and Families Center, etc.' },
    { id: 'c26', text: 'Do not rush combining breast milk and formula', category: 'body', note: 'If breast milk is insufficient, supplement with formula. Consult OB/GYN or midwife.' },
    { id: 'c27', text: 'Thoroughly prevent secondhand smoke at home', category: 'life', note: 'No smoking near pregnant women or infants.' },
    { id: 'c28', text: 'Keep mama\'s meals balanced at about +450 kcal', category: 'body', note: 'Base on one soup and three dishes, with iron and calcium in mind.' },
  ],
  shopping: [
    { id: 's1', name: 'Baby carrier (newborn compatible)', priority: 'should', timing: '0–3 months postpartum', priceYenMin: 3000, priceYenMax: 15000, note: 'Check pre-neck-support compatibility.' },
    { id: 's2', name: 'Nursing bra (postpartum size)', priority: 'must', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Prioritize comfort.' },
    { id: 's3', name: 'Baby wipes (large pack)', priority: 'must', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Low-irritation type for newborns.' },
    { id: 's4', name: 'Baby bath towel', priority: 'should', timing: '0–3 months postpartum', priceYenMin: 1000, priceYenMax: 3000, note: 'Gauze material is popular.' },
    { id: 's5', name: 'Baby monitor', priority: 'nice', timing: '0–3 months postpartum', priceYenMin: 5000, priceYenMax: 20000, note: 'Many types include camera.' },
    { id: 's6', name: 'Nursing cover', priority: 'should', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 5000, note: 'Convenient when going out.' },
    { id: 's7', name: 'Postpartum underwear and pelvic belt', priority: 'should', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Follow doctor or midwife guidance.' },
    { id: 's8', name: 'Breast pump (electric or manual)', priority: 'nice', timing: '0–3 months postpartum', priceYenMin: 3000, priceYenMax: 20000, note: 'Choose according to milk amount and lifestyle.' },
    { id: 's9', name: 'Baby nail clippers', priority: 'should', timing: '0–3 months postpartum', priceYenMin: 500, priceYenMax: 2000, note: 'Safety design for newborns.' },
    { id: 's10', name: 'Relaxation items for mama', priority: 'nice', timing: '0–3 months postpartum', priceYenMin: 1000, priceYenMax: 5000, note: 'Bath additive, eye mask, etc.' },
    { id: 's11', name: 'Diaper pail', priority: 'nice', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 6000, note: 'Odor control.' },
    { id: 's12', name: 'Bottle sterilizer', priority: 'nice', timing: '0–3 months postpartum', priceYenMin: 2000, priceYenMax: 8000, note: 'Choose microwave, liquid, or boiling type.' },
  ],
  warnings: [
    { id: 'w1', label: 'Large amount of lochia', level: 'urgent' },
    { id: 'w2', label: 'Fever of 39°C or higher', level: 'urgent' },
    { id: 'w3', label: 'Chest pain', level: 'urgent' },
    { id: 'w4', label: 'Feeling drowsy or losing consciousness', level: 'urgent' },
    { id: 'w5', label: 'Baby has difficulty breathing', level: 'urgent' },
    { id: 'w6', label: 'Change in smell or increase in lochia', level: 'watch' },
    { id: 'w7', label: 'Wound pain', level: 'watch' },
    { id: 'w8', label: 'Breastfeeding pain', level: 'watch' },
    { id: 'w9', label: 'Feeling down', level: 'watch' },
    { id: 'w10', label: 'Baby has decreased appetite', level: 'watch' },
    { id: 'w11', label: 'Umbilical bleeding or pus', level: 'watch' },
    { id: 'w12', label: 'Jaundice is strong or prolonged', level: 'watch' },
  ],
  partner: [
    { id: 'p1', text: 'Support nighttime feedings' },
    { id: 'p2', text: 'Lead housework and let mama rest' },
    { id: 'p3', text: 'Observe mama and encourage her to consult' },
    { id: 'p4', text: 'Learn diaper changes and bathing together' },
    { id: 'p5', text: 'Make sleep time for mama on weekends' },
    { id: 'p6', text: 'Consult together about childcare worries' },
    { id: 'p7', text: 'Accompany vaccinations and checkups' },
    { id: 'p8', text: 'Make time for the two of you' },
    { id: 'p9', text: 'Take postpartum papa childcare leave' },
  ],
  tips: [
    { id: 't1', title: 'Sleep "together with the baby"', body: 'Night feedings tend to cut sleep. If you lie down when the baby sleeps during the day, recovery differs.' },
    { id: 't2', title: 'Consult early about breastfeeding worries', body: 'Nipple pain or signs of mastitis can be consulted at a breastfeeding clinic or with a midwife. You do not have to endure it alone.' },
    { id: 't3', title: 'Keep housework to "minimum"', body: 'Minimize cleaning and laundry. Use ready-made meals and delivery. Postpartum mama is still recovering.' },
    { id: 't4', title: 'Value your pace when going out', body: 'Start with about 1–2 hours. Choose facilities with nursing spaces, and get used to it without overdoing it.' },
    { id: 't5', title: 'Consult early for postpartum depression', body: 'This is a period when mood tends to drop. Do not blame yourself; use OB/GYN, psychiatry, or municipal consultation windows.' },
    { id: 't6', title: 'Asking for help with childcare is a strength', body: 'You can use Niigata City Midwives Association "Hello Midwife," Children and Families Centers (Hot Station), and LINE consultation.' },
    { id: 't7', title: 'Combining breast milk and formula is okay', body: 'Do not be too fixated on breastfeeding. Choose the method with less stress for mama, and consult a midwife or OB/GYN.' },
    { id: 't8', title: 'Meals are about +450 kcal during breastfeeding', body: 'Base on one soup and three dishes, with iron and calcium in mind. Hydrate well during breastfeeding.' },
    { id: 't9', title: 'Prevent secondhand smoke', body: 'Make a rule not to smoke near infants. Including heated tobacco, ventilation fans do not prevent sidestream smoke.' },
    { id: 't10', title: 'For sudden illness, call #8000', body: 'Via the Niigata City and other children\'s medical telephone consultation #8000, you can consult nurses on nights and holidays.' },
  ],
  adminGuide: [
    { id: 'a1', title: 'Birth notification submission deadline', body: 'Submit to the city/ward office within 14 days after birth. Either the husband or wife can submit. Maternity handbook and checkup vouchers are received together.', when: 'Within 14 days after birth' },
    { id: 'a2', title: 'Application for child allowance', body: 'For 0-year-olds, 15,000 yen per month until age 3 (income limits apply). Apply at the municipal children\'s section. There may be reductions or non-payment depending on income.', when: 'Immediately after birth' },
    { id: 'a3', title: 'Issuance of infant medical certificate', body: 'Presenting it at pediatric visits gives you a medical expense subsidy. Eligible age and content vary by municipality.', when: 'After birth notification submission' },
    { id: 'a4', title: 'Vaccination pre-examination tickets', body: 'Follow the schedule recorded in the maternity handbook. Consider optional vaccines such as rotavirus, Hib, and pneumococcus.', when: 'From around 1–2 months' },
    { id: 'a5', title: 'Postpartum health checkup', body: 'Receive at 6–8 weeks postpartum. Check uterine recovery, anemia, blood pressure, breasts, and pelvic floor. Free municipal checkups may also be available.', when: '6–8 weeks postpartum' },
    { id: 'a6', title: 'Preparation for returning from childcare leave', body: 'Start coordinating with the company 3–4 months before returning. Also discuss nursery application and working style.', when: 'Around 2–3 months postpartum' },
    { id: 'a7', title: 'Application for child medical expense subsidy', body: 'Subsidizes the out-of-pocket portion of children\'s medical expenses (insured treatment). Varies by municipality. Apply after joining health insurance.', when: 'Promptly after birth' },
    { id: 'a8', title: 'Niigata Prefecture Komusubi fixed-term deposit', body: 'For children under 1 born in Niigata Prefecture. A 100,000-yen fixed-term deposit is provided. Apply through the prefecture\'s electronic application system by the day before the first birthday.', when: 'By the day before the first birthday' },
    { id: 'a9', title: 'Use of postpartum care services', body: 'Niigata City offers overnight, day, and visiting postpartum care. If already applied, use it; if not, consult.', when: 'Postpartum to 6 months' },
    { id: 'a10', title: 'Father\'s childbirth childcare leave', body: 'Up to 4 weeks can be taken within 8 weeks after birth. Benefits are paid from employment insurance. Notify the company by 2 weeks before the start date.', when: 'Within 8 weeks after birth' },
  ],
  careScenes: [
    {
      id: 'cs1',
      title: 'Putting to sleep at night',
      items: [
        'Dark and quiet the room.',
        'Create a flow of diaper change, feeding, and burping.',
        'Use white noise or gentle voice to calm.',
        'Day-night rhythm is not yet established; accept this.',
        'Make time for mama to rest too.',
      ],
    },
    {
      id: 'cs2',
      title: 'Breastfeeding and formula',
      items: [
        'Breast milk is on demand; mama\'s condition is also important.',
        'Make formula at the correct amount, temperature, and concentration.',
        'Burp after feeding.',
        'Consult if nipple pain continues.',
        'Do not forget hydration.',
      ],
    },
    {
      id: 'cs3',
      title: 'Diaper change',
      items: [
        'Gently wipe the bottom every time.',
        'Apply a thin layer of moisturizer to prevent diaper rash.',
        'For boys and girls, wipe from front to back.',
        'Never look away from the changing table.',
        'Wash hands immediately.',
      ],
    },
    {
      id: 'cs4',
      title: 'Going out',
      items: [
        'Confirm child seat installation.',
        'Bring extra diapers, wipes, and changes of clothes.',
        'Choose facilities with nursing spaces.',
        'Keep outings short.',
        'Mama also ensures hydration and rest.',
      ],
    },
  ],
};

export const postpartumLateEn: Stage = {
  id: 'postpartum-late',
  key: '6',
  title: 'Postpartum 3–12 Months',
  subtitle: 'Enjoy the new everyday life',
  weeks: '3–12 months postpartum',
  description:
    'The baby\'s growth is remarkable. While recovering yourself and regaining couple time, move to the next step.',
  mindCards: [
    {
      id: 'm1',
      title: 'What you could do today',
      body: 'Acknowledge what you could do today, not perfection.',
      type: 'mind',
    },
    {
      id: 'm2',
      title: 'Watch your own recovery',
      body: 'Resume gentle exercise without overdoing it, and continue pelvic and core care.',
      type: 'body',
    },
    {
      id: 'm3',
      title: 'Plan weaning and vaccinations',
      body: 'Advance the 6-month, 9-month, and 1-year checkups and vaccinations in a planned way.',
      type: 'life',
    },
    {
      id: 'm4',
      title: 'Regain couple time',
      body: 'Value connection with your partner and make even short time to talk.',
      type: 'mind',
    },
    {
      id: 'm5',
      title: 'Adjust childcare and work',
      body: 'Advance returning to work and nursery preparations, and find a way to work that suits you.',
      type: 'life',
    },
    {
      id: 'm6',
      title: 'Re-adjust partner roles',
      body: 'Review household and childcare division according to growth, and support each other.',
      type: 'partner',
    },
    {
      id: 'm7',
      title: 'Go to local play areas',
      body: 'Use child-rearing support centers and parks for outside stimulation and mom friends. In Niigata City, the SKIP play map is also useful.',
      type: 'life',
    },
    {
      id: 'm8',
      title: 'Do not hold everything alone',
      body: 'Knowing you can use Family Support or temporary childcare makes your heart lighter.',
      type: 'mind',
    },
  ],
  checklist: [
    { id: 'c1', text: 'Make appointments for infant general checkups (3–4 months, 10 months) and 1.5-year checkup', category: 'hospital', note: 'Physical measurements and development checks, weaning consultation. Example: Niigata City. Schedule varies by municipality.' },
    { id: 'c2', text: 'Prepare for starting weaning', category: 'life', note: 'Around 5–6 months. Start with smoothly mashed 10-fold rice porridge.' },
    { id: 'c3', text: 'Check vaccination progress', category: 'hospital', note: 'BCG, rotavirus, Hib, pneumococcus, etc. Check the schedule in the maternity handbook.' },
    { id: 'c4', text: 'Confirm dependent deduction and tax procedures', category: 'admin', note: 'Check documents needed for year-end adjustment or tax filing.' },
    { id: 'c5', text: 'Advance preparation for returning from childcare leave', category: 'admin', note: 'Adjust working hours and nursery pickup/drop-off. Check Papa Mama Childcare Leave Plus.' },
    { id: 'c6', text: 'Plan nursery application and tours', category: 'admin', note: 'Check application period and admission criteria on the municipal website. Niigata City\'s SKIP pamphlet is also a reference.' },
    { id: 'c7', text: 'Make dental checkup appointments for mama and baby', category: 'hospital', note: 'Visit the dentist once baby teeth start coming in.' },
    { id: 'c8', text: 'Resume gentle exercise without overdoing it', category: 'body', note: 'Start with pelvic floor and light stretching.' },
    { id: 'c9', text: 'Revive your hobbies and personal time little by little', category: 'mind', note: 'Even a short time for yourself.' },
    { id: 'c10', text: 'Make time to talk as a couple', category: 'partner', note: 'Talk about topics other than childcare too.' },
    { id: 'c11', text: 'Continue pelvic and core care', category: 'body', note: 'Do not rush postpartum body shape; continue steadily.' },
    { id: 'c12', text: 'Remember postpartum depression consultation contacts (general names)', category: 'mind', note: 'Psychiatry, psychosomatic medicine, Children and Families Centers in each ward, etc.' },
    { id: 'c13', text: 'Use local child-rearing support centers', category: 'mind', note: 'Example in Niigata City: Churip, Aiai, etc. Play, parent interaction, and consultation.' },
    { id: 'c14', text: 'Review role division with your partner', category: 'partner', note: 'Weaning, bathing, nighttime response, etc.' },
    { id: 'c15', text: 'Prepare for the shrine visit and events', category: 'life', note: 'Clothes, schedule, amulet, etc.' },
    { id: 'c16', text: 'Enjoy baby signs and language development', category: 'life', note: 'Through chatting and picture books.' },
    { id: 'c17', text: 'Review disaster preparedness', category: 'life', note: 'Emergency formula, diapers, contacts.' },
    { id: 'c18', text: 'Value mama\'s own relationships', category: 'mind', note: 'Interaction with friends and moms of the same generation.' },
    { id: 'c19', text: 'Prepare toys and environment appropriate for growth', category: 'shopping', note: 'Check safety standards. Also prevent accidental ingestion, falls, and SIDS.' },
    { id: 'c20', text: 'Talk about plans for a second child if needed', category: 'partner', note: 'Share timing, health, and life plans.' },
    { id: 'c21', text: 'Continue to confirm receipt of child medical expense subsidy', category: 'admin', note: 'Example: Niigata City. Report changes when moving or changing insurance.' },
    { id: 'c22', text: 'Research Family Support Centers', category: 'admin', note: 'Example: Niigata City. Paid volunteer for pickup and temporary care.' },
    { id: 'c23', text: 'Confirm temporary childcare and short-stay services', category: 'admin', note: 'For refreshment or emergencies. Some facilities accept from a few months old.' },
    { id: 'c24', text: 'Confirm the Niigata Prefecture Komusubi fixed-term deposit application deadline', category: 'admin', note: 'For children under 1 born in Niigata Prefecture. By the day before the first birthday.' },
    { id: 'c25', text: 'Confirm regular pediatrician and #8000', category: 'hospital', note: 'For nights and holidays, use the children\'s medical telephone consultation #8000 (example: Niigata City).' },
    { id: 'c26', text: 'Do not give honey to children under 1 year old', category: 'body', note: 'Prevention of infant botulism. Also check ingredient labels of processed foods.' },
  ],
  shopping: [
    {
      id: 's1',
      name: 'Weaning set (bowl, spoon, apron)',
      priority: 'should',
      timing: 'Around 5–6 months',
      priceYenMin: 2000,
      priceYenMax: 8000,
      note: 'Suction bowls and silicone spoons are convenient.',
    },
    {
      id: 's2',
      name: 'Baby chair',
      priority: 'should',
      timing: 'Around 5–6 months',
      priceYenMin: 5000,
      priceYenMax: 25000,
      note: 'For weaning and family meals.',
    },
    {
      id: 's3',
      name: 'Baby toys',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 1000,
      priceYenMax: 5000,
      note: 'Things that make sound and cloth picture books.',
    },
    {
      id: 's4',
      name: 'Stroller replacement candidate',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 20000,
      priceYenMax: 60000,
      note: 'Consider switching once the baby can sit.',
    },
    {
      id: 's5',
      name: 'Exercise wear for mama',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 3000,
      priceYenMax: 10000,
      note: 'For starting gentle exercise.',
    },
    {
      id: 's6',
      name: 'Childcare item storage',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 2000,
      priceYenMax: 8000,
      note: 'For organizing toys and weaning items.',
    },
    {
      id: 's7',
      name: 'Toothbrush and dental care items',
      priority: 'should',
      timing: 'Around 6 months',
      priceYenMin: 1000,
      priceYenMax: 3000,
      note: 'Start once baby teeth appear.',
    },
    {
      id: 's8',
      name: 'Baby food and weaning ingredients',
      priority: 'should',
      timing: 'Around 5–6 months',
      priceYenMin: 2000,
      priceYenMax: 6000,
      note: 'Start with a small amount. Check for allergies.',
    },
    {
      id: 's9',
      name: 'Baby gate and safety goods',
      priority: 'should',
      timing: '8–12 months',
      priceYenMin: 3000,
      priceYenMax: 15000,
      note: 'For crawling and standing with support.',
    },
    {
      id: 's10',
      name: 'Picture books',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 1000,
      priceYenMax: 5000,
      note: 'Start with cloth books and board books.',
    },
    {
      id: 's11',
      name: 'Backpack or bag for mama',
      priority: 'nice',
      timing: '3–12 months',
      priceYenMin: 3000,
      priceYenMax: 12000,
      note: 'A backpack that frees both hands is also convenient for outings.',
    },
    {
      id: 's12',
      name: 'Diapers (M and L sizes)',
      priority: 'must',
      timing: '3–12 months',
      priceYenMin: 4000,
      priceYenMax: 12000,
      note: 'Size up as the baby grows.',
    },
  ],
  warnings: [
    { id: 'w1', label: 'Febrile seizure', level: 'urgent' },
    { id: 'w2', label: 'Abnormal consciousness', level: 'urgent' },
    { id: 'w3', label: 'Abnormal breathing', level: 'urgent' },
    { id: 'w4', label: 'Development check and growth curve', level: 'watch' },
    { id: 'w5', label: 'Decreased appetite', level: 'watch' },
    { id: 'w6', label: 'Mama feels down or exhausted', level: 'watch' },
    { id: 'w7', label: 'Allergic reaction to weaning food', level: 'watch' },
    { id: 'w8', label: 'Fever of 38°C or higher', level: 'watch' },
  ],
  partner: [
    { id: 'p1', text: 'Reconfirm childcare roles' },
    { id: 'p2', text: 'Make time to be together as a couple' },
    { id: 'p3', text: 'Continue supporting mama\'s rest' },
    { id: 'p4', text: 'Do weaning and bathing together' },
    { id: 'p5', text: 'Advance nursery and return-to-work plans together' },
    { id: 'p6', text: 'Accompany vaccinations and checkups' },
    { id: 'p7', text: 'Observe mama and encourage her to consult' },
    { id: 'p8', text: 'Make even short time for the two of you' },
  ],
  tips: [
    { id: 't1', title: 'Start weaning with "small amount, single ingredient"', body: 'Around 5–6 months, start with smoothly mashed 10-fold rice porridge. Progress according to age: tongue-mashable, then gum-mashable. Start with 1 spoonful and observe for 3–5 days.' },
    { id: 't2', title: 'Vaccination appointments can be hard to get', body: 'BCG and rotavirus have limited timing, so book early. Check the schedule in the maternity handbook so you do not forget.' },
    { id: 't3', title: 'Gather information on returning to work and nursery early', body: 'Check nursery application period and the difference between licensed and unlicensed nurseries on the municipal website. Niigata City\'s child-rearing support pamphlet "SKIP" has a list of nurseries and a play map (varies by municipality).' },
    { id: 't4', title: 'Couple time is about "quality" not "length"', body: 'Even 15 minutes eating together or talking after a bath matters. Regularly review childcare division too.' },
    { id: 't5', title: 'Review accident prevention by age', body: 'Regularly check accidental ingestion, falls, bathtub, and child seat (mandatory until under 6). Avoid prone sleeping and secondhand smoke to reduce SIDS risk.' },
    { id: 't6', title: 'If unsure, call #8000', body: 'On nights and holidays when you are unsure whether to seek care, you can consult nurses via the children\'s medical telephone consultation #8000 in Niigata City and other areas.' },
    { id: 't7', title: 'No honey for children under 1 year', body: 'To prevent infant botulism, do not give honey or honey-containing foods until age 1.' },
  ],
  adminGuide: [
    {
      id: 'a1',
      title: 'Nursery application',
      body: 'Licensed nurseries require application to the municipality. Check application period, admission criteria, and required documents, and plan tours too. Niigata City\'s SKIP guide is also a reference (varies by municipality).',
      when: 'From around 3–6 months postpartum',
    },
    {
      id: 'a2',
      title: 'Return-to-work procedures from childcare leave',
      body: 'Contact the company 3–4 weeks before the return date. Adjust working hours, breaks, and short-time work. If both parents take leave, check Papa Mama Childcare Leave Plus, which extends until the child is 1 year and 2 months.',
      when: '1–2 months before returning',
    },
    {
      id: 'a3',
      title: 'Dependent deduction and tax procedures',
      body: 'Year-end adjustment or tax filing may allow dependent deductions and medical expense deductions. Organize receipts and maternity handbook copies.',
      when: 'Year-end adjustment or tax filing season',
    },
    {
      id: 'a4',
      title: 'Vaccination pre-examination tickets',
      body: 'Follow the schedule recorded in the maternity handbook. Also confirm cost and timing of optional vaccines, and consult a pediatrician.',
      when: 'Around 1–12 months',
    },
    {
      id: 'a5',
      title: 'Continued procedures for child allowance',
      body: 'Continues until junior high third year. Report changes to the municipality when income, residence, or other changes occur.',
      when: 'Whenever changes occur',
    },
    {
      id: 'a6',
      title: 'Infant general health checkup',
      body: 'Growth and development are checked at 3–4 months, 10 months (infant general checkups I and II), 1.5 years, 3 years, etc. You can also consult about weaning and childcare (example: Niigata City; varies by municipality).',
      when: 'At the relevant age',
    },
    {
      id: 'a7',
      title: 'Child medical expense subsidy',
      body: 'Subsidizes the out-of-pocket portion of children\'s medical expenses (insured treatment). Report changes when moving or changing insurance (example: Niigata City).',
      when: 'As needed or when changes occur',
    },
    {
      id: 'a8',
      title: 'Family Support Center',
      body: 'A paid volunteer service connecting people who need childcare help with those who want to help. Can be used for pickup and temporary care (example: Niigata City).',
      when: 'When childcare is needed',
    },
    {
      id: 'a9',
      title: 'Temporary childcare and short stay',
      body: 'Services to temporarily leave your child for refreshment, illness, ceremonies, etc. Overnight short stay (short-term childcare support) is also available (varies by municipality).',
      when: 'When needed',
    },
    {
      id: 'a10',
      title: 'Niigata Prefecture Komusubi fixed-term deposit',
      body: 'For children under 1 born in Niigata Prefecture. A 100,000-yen fixed-term deposit is provided. Apply through the prefecture\'s electronic application system by the day before the first birthday.',
      when: 'By the day before the first birthday',
    },
    {
      id: 'a11',
      title: 'Children\'s medical telephone consultation (#8000)',
      body: 'In Niigata City and elsewhere, nurses are available for consultation on nights/holidays when you are unsure whether to seek care for your child.',
      when: 'Emergency or night/holiday',
    },
  ],
  careScenes: [
    {
      id: 'cs1',
      title: 'Weaning',
      items: [
        'Prepare bowl, apron, and spoon.',
        'Start with 1 spoonful and a single ingredient, observing for 3–5 days.',
        'Introduce allergenic foods in small amounts.',
        'Do not force the baby to eat leftovers.',
        'Do not forget hydration after meals.',
      ],
    },
    {
      id: 'cs2',
      title: 'Going out',
      items: [
        'Bring extra diapers, wipes, and changes of clothes.',
        'Choose facilities with nursing spaces or baby rooms.',
        'Adjust outing length to mama and baby\'s condition.',
        'Mama also ensures hydration and rest.',
        'Avoid crowds and long trips.',
      ],
    },
    {
      id: 'cs3',
      title: 'Putting to sleep at night',
      items: [
        'Create a calm time before bed.',
        'Establish a fixed flow of bath, feeding, and tooth brushing.',
        'For night crying, sometimes wait a moment before responding.',
        'Keep the bedroom dark and at a comfortable temperature.',
        'Mama also rests without overdoing it.',
      ],
    },
    {
      id: 'cs4',
      title: 'Play and stimulation',
      items: [
        'Incorporate chatting and singing into daily life.',
        'Use cloth picture books and sound-making toys to stimulate the senses.',
        'Do tummy time while watching for short periods.',
        'Make time to feel outside air and greenery.',
        'Prepare a safe environment according to growth.',
      ],
    },
  ],
};

export const enStages: Stage[] = [
  pregnancy1En,
  pregnancy2En,
  pregnancy3En,
  birthEn,
  postpartumEarlyEn,
  postpartumLateEn,
];
